import Logger from 'wanderu-logger';

import currencyRateCacheService from './caching/currencyRateCacheService';
import currencyApiService from './dataAccess/currencyApiService';

const logger = Logger.getLogger('currencyService');

/* the exchange-rate map given to us by wapi looks like:
 *
 * { <baseCurrency>: { <baseCurrency>: 1 , "USD": x.xx , "EUR": x.xx , "CAD": x.xx , ... }}
 *
 * `baseCurrency` is set via the URL or the header's currency menu. `x.xx` is an amount
 * of quote currency: some number of units of a non-base currency which is equivalent to
 * one unit of base currency.
 */
function convertPrice(price, fromCurrency, toCurrency) {
    if (fromCurrency === toCurrency) {
        return price;
    }

    const inverseConversionRate = _getInverseConversionRate(fromCurrency, toCurrency);
    return price / inverseConversionRate;
}

function _getInverseConversionRate(fromCurrency, toCurrency) {
    const conversionRates = currencyRateCacheService.getConversionRatesForCurrency(toCurrency);

    if (!conversionRates) {
        let errorMessage = `Conversion rates have not been loaded for ${toCurrency}`;
        logger.error(errorMessage);
        throw new Error(errorMessage);
    }

    return conversionRates[fromCurrency];
}

async function loadConversionRatesForCurrency(currency) {
    const cachedConversionRates = currencyRateCacheService.getConversionRatesForCurrency(currency);

    if (cachedConversionRates) {
        return;
    }

    const conversionRates = await _fetchConversionRatesForCurrency(currency);
    currencyRateCacheService.setConversionRatesForCurrency(currency, conversionRates);
    return conversionRates;
}

async function _fetchConversionRatesForCurrency(currency) {
    const supportedCurrencies = await _getSupportedCurrencies();

    try {
        return await currencyApiService.getConversionRates(currency, supportedCurrencies);
    } catch (ex) {
        logger.error('_fetchConversionRatesForCurrency', ex);
        throw ex;
    }
}

async function _getSupportedCurrencies() {
    const cachedSupportedCurrencies = currencyRateCacheService.getSupportedCurrencies();

    if (cachedSupportedCurrencies) {
        return cachedSupportedCurrencies;
    }

    const supportedCurrencies = await _fetchSupportedCurrencies();
    currencyRateCacheService.setSupportedCurrencies(supportedCurrencies);
    return supportedCurrencies;
}

async function _fetchSupportedCurrencies() {
    try {
        return await currencyApiService.getSupportedCurrencies();
    } catch (ex) {
        logger.error('_fetchSupportedCurrencies', ex);
        throw ex;
    }
}

function getAllConversionRates() {
    return currencyRateCacheService.getAllConversionRates();
}

function getSupportedCurrencies() {
    return currencyRateCacheService.getSupportedCurrencies();
}

function hydrate(supportedCurrencies, conversionRates) {
    currencyRateCacheService.setSupportedCurrencies(supportedCurrencies);

    Object.entries(conversionRates).forEach(([currency, rates]) => {
        currencyRateCacheService.setConversionRatesForCurrency(currency, rates);
    });
}

export {
    convertPrice,
    getAllConversionRates,
    getSupportedCurrencies,
    hydrate,
    loadConversionRatesForCurrency,
};

export default {
    hydrate,
    convertPrice,
    loadConversionRatesForCurrency,
    getAllConversionRates,
    getSupportedCurrencies,
};
